import React, { Fragment, useState, useEffect, useCallback } from "react";
import {
  Grid,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Tabs,
  Tab,
  Divider,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from "@mui/material";
import { ListingHeader, ContainerCard, NormalTextField } from "../../components";
import { makeStyles } from "@mui/styles";
import { api, urls } from "../../services";
import { useAuth, useToast } from "../../contexts";
import { debounce } from "lodash";
import moment from "moment";
import NoDataCard from "../../components/cards/NoDataCard";
import LoaderCard from "../../components/cards/LoaderCard";

const useStyles = makeStyles((theme) => ({
  tab: {
    "&.MuiTab-root": {
      textTransform: "none",
      fontSize: 20,
      fontWeight: 600,
      "&.Mui-selected": {
        color: theme.palette.common.redBtn,
      },
    },
  },
}));

const TAB_FILTERS = ["pending", "approved", "rejected"];

export function PasscodeResets() {
  const classes = useStyles();
  const theme = useTheme();
  const { logout } = useAuth();
  const { showToast } = useToast();

  const [tabValue, setTabValue] = useState(0);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPage, setTotalPages] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [adminNote, setAdminNote] = useState("");

  const handleTabChange = (e, v) => {
    setTabValue(v);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getData = () => {
    setIsLoading(true);
    api
      .get(urls.passcodeResetList, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          filter_key: TAB_FILTERS[tabValue],
        },
      })
      .then((res) => {
        setData(res.data.data.requests);
        setTotalPages(res.data.data.pagination.total);
        setPendingCount(res.data.data.pending_count);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, tabValue]);

  const openConfirm = (item, action) => {
    setConfirmItem(item);
    setConfirmAction(action);
    setAdminNote("");
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!confirmItem || !confirmAction) return;
    api
      .post(urls.resetPasscode + confirmItem.id, {
        action: confirmAction,
        admin_note: adminNote,
      })
      .then((res) => {
        showToast(res.data.message, "success");
        setConfirmOpen(false);
        getData();
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  return (
    <Fragment>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <ListingHeader
            title={
              pendingCount > 0
                ? `Passcode Resets (${pendingCount} pending)`
                : "Passcode Resets"
            }
            placeholder="Search"
          />
        </Grid>
        {isLoading && (
          <Grid item>
            <LoaderCard />
          </Grid>
        )}
        {!isLoading && (
          <Grid item>
            <ContainerCard>
              <Stack>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    sx: { backgroundColor: theme.palette.common.redBtn },
                  }}
                >
                  <Tab
                    className={classes.tab}
                    label={
                      pendingCount > 0
                        ? `Pending (${pendingCount})`
                        : "Pending"
                    }
                  />
                  <Tab className={classes.tab} label="Approved" />
                  <Tab className={classes.tab} label="Rejected" />
                </Tabs>
                <Divider />
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                          Name
                        </TableCell>
                        <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                          Phone
                        </TableCell>
                        <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                          Reason
                        </TableCell>
                        <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                          Date
                        </TableCell>
                        {tabValue !== 0 && (
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Admin Note
                          </TableCell>
                        )}
                        {tabValue === 0 && (
                          <TableCell
                            sx={{ fontSize: 16, fontWeight: 600 }}
                            align="right"
                          >
                            Actions
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((item, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{item.first_name}</TableCell>
                          <TableCell>{`${item.phone_code || ""} ${
                            item.phone_number || ""
                          }`}</TableCell>
                          <TableCell>{item.reason || "-"}</TableCell>
                          <TableCell>
                            {moment(item.created_at).format("DD/MM/YYYY")}
                          </TableCell>
                          {tabValue !== 0 && (
                            <TableCell>{item.admin_note || "-"}</TableCell>
                          )}
                          {tabValue === 0 && (
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                sx={{ mr: 1, textTransform: "none" }}
                                onClick={() => openConfirm(item, "approve")}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="error"
                                sx={{ textTransform: "none" }}
                                onClick={() => openConfirm(item, "reject")}
                              >
                                Reject
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {data.length === 0 && !isLoading && (
                  <NoDataCard title={"No Data Found"} />
                )}
                <TablePagination
                  sx={{ mt: 2 }}
                  component="div"
                  count={totalPage}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Stack>
            </ContainerCard>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmAction === "approve"
            ? "Approve Passcode Reset"
            : "Reject Passcode Reset"}
        </DialogTitle>
        <DialogContent>
          {confirmItem && (
            <p>
              {confirmAction === "approve"
                ? `Approve reset for ${confirmItem.first_name}? Their passcode will be cleared and they will set a new one on next login.`
                : `Reject reset request from ${confirmItem.first_name}?`}
            </p>
          )}
          <NormalTextField
            title="Admin Note (optional)"
            placeholder="Add a note..."
            value={adminNote}
            onTextChange={(e) => setAdminNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmAction === "approve" ? "success" : "error"}
            onClick={handleConfirm}
            sx={{ textTransform: "none" }}
          >
            {confirmAction === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
